import {
  defineAbilityFor,
  setAreaDirectory,
  type AppAbility,
  type AreaCode,
  type AreaInfo,
  type RoleCode,
  type RoleMatrix,
  type SessionUser,
} from '@ftthnms/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';

export interface MeResponse {
  user: SessionUser;
  primaryRole: RoleCode | null;
  areas: 'all' | AreaCode[];
  /** Role → permission codes, edited by admins (Pengguna & Peran). */
  permissions: RoleMatrix;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/v1/auth/me'),
    // A 5xx or network error usually means the API is restarting after a deploy: keep trying for ~1 minute.
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 20,
    retryDelay: 3000,
    staleTime: 60_000,
  });
}

/**
 * Names, prefixes and colours of all areas. Loaded once per session and written into the shared
 * directory so `areaName()` and `RegionChip` know areas added in Settings.
 */
export function useAreaDirectory() {
  const q = useQuery({
    queryKey: ['area-directory'],
    queryFn: () => api<AreaInfo[]>('/api/v1/areas/directory'),
    staleTime: 5 * 60_000,
  });
  if (q.data) setAreaDirectory(q.data);
  return q;
}

interface SessionContextValue {
  me: MeResponse;
  ability: AppAbility;
  /** Area picked in the topbar (null = all the user may see). Technicians with one area are locked to it. */
  area: AreaCode | null;
  setArea: (a: AreaCode | null) => void;
  areaOptions: AreaCode[] | 'all';
  areaLocked: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);
const AREA_KEY = 'fnms-area';

export function SessionProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const ability = useMemo(() => defineAbilityFor(me.user, me.permissions), [me.user, me.permissions]);
  const areaLocked = me.areas !== 'all' && me.areas.length === 1;

  const [picked, setPicked] = useState<AreaCode | null>(() => {
    try {
      return (localStorage.getItem(AREA_KEY) as AreaCode | null) || null;
    } catch {
      return null;
    }
  });

  // Never keep a stored area the user is not allowed to see.
  const area: AreaCode | null = areaLocked
    ? (me.areas as AreaCode[])[0]!
    : picked && (me.areas === 'all' || me.areas.includes(picked))
      ? picked
      : null;

  const setArea = (a: AreaCode | null) => {
    setPicked(a);
    try {
      if (a) localStorage.setItem(AREA_KEY, a);
      else localStorage.removeItem(AREA_KEY);
    } catch {
      /* ignore */
    }
  };

  return (
    <SessionContext.Provider value={{ me, ability, area, setArea, areaOptions: me.areas, areaLocked }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession dipakai di luar SessionProvider');
  return ctx;
}

export const ROLE_LABEL: Record<RoleCode, string> = {
  admin: 'Admin',
  noc: 'NOC',
  planner: 'Planner',
  teknisi: 'Teknisi',
};

/**
 * Local logout by default; `global` also signs out of Speedmax ID (every Speedmax app).
 * `revokeAgents` disconnects the user's AI agents too (a global logout always does).
 */
export async function logout(global = false, revokeAgents = false): Promise<void> {
  const { redirect } = await api<{ redirect: string }>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ global, revokeAgents }),
  });
  window.location.assign(redirect);
}
